/**
 * Phase 5: Overnight forecast card — expected kWh at sunrise, confidence band, trust copy.
 * For grid_primary: shows Backup Readiness (current kWh if grid fails). See GRID_MODES_AND_FORECAST_SEMANTICS.
 * Gated by readiness: connecting = hidden; warming_up = low confidence; learning = cap medium; ready = full; degraded = hidden.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Moon, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { OvernightForecastResult } from '@/hooks/useOvernightForecast';
import type { ReadinessState } from '@/lib/readiness';
import { resilienceCopy } from '@/lib/inline-copy';

/** Runtime = current_kWh / load_kW hours. Cap at 72h. If load <= epsilon, return null (caller shows "Load minimal"). */
export const LOAD_EPSILON_KW = 0.01;
export const RUNTIME_CAP_HOURS = 72;

export function formatBackupReadinessRuntime(availableKwh: number, loadKw: number): string | null {
  if (loadKw <= LOAD_EPSILON_KW || !Number.isFinite(loadKw) || loadKw <= 0) {
    return null;
  }
  const hoursRaw = availableKwh / loadKw;
  const hours = Math.min(hoursRaw, RUNTIME_CAP_HOURS);
  if (!Number.isFinite(hours) || hours <= 0) return null;
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h >= 72) return `~72h+ at current load`;
  if (h >= 1) return `~${h}h ${m}m at current load`;
  return `~${m}m at current load`;
}

function confidenceLabel(c: 'low' | 'medium' | 'high', proven: boolean): string {
  // WT-337: an unproven property (no realized-accuracy history yet) is NOT "low confidence" — that
  // conflates a healthy new install with a measured-inaccurate one. Show a neutral baseline state.
  if (!proven) return 'Establishing baseline — building accuracy history';
  if (c === 'high') return 'High confidence';
  if (c === 'medium') return 'Medium confidence';
  // proven-low = measured inaccurate (not "limited data").
  return "Low confidence — this system's forecasts have been off recently";
}

function capConfidence(
  confidence: 'low' | 'medium' | 'high',
  readinessState: ReadinessState,
  confidenceBasis: string | null,
): 'low' | 'medium' | 'high' {
  if (readinessState === 'warming_up') return 'low';
  if (readinessState === 'learning' && confidence === 'high') return 'medium';
  // WT-337 interim safety: the legacy load-coverage confidence is anti-calibrated (measured "high"
  // carried +3 kWh optimism). Until the empirical basis is live, never show a green "High" survival
  // badge on a coverage-derived label. Self-lifts once confidence_basis === 'empirical'.
  if (confidence === 'high' && confidenceBasis !== 'empirical') return 'medium';
  return confidence;
}

export function OvernightForecastCard({
  data,
  loading,
  error,
  readinessState = 'ready',
  gridMode,
  availableKwh,
  loadKw,
  resilienceRisk,
  resilienceSeverity,
  runwaySunriseKwh,
}: {
  data: OvernightForecastResult | null | undefined;
  loading: boolean;
  error: Error | null;
  readinessState?: ReadinessState;
  /** When grid_primary, show Backup Readiness instead of Overnight outlook. */
  gridMode?: 'off_grid' | 'grid_primary' | null;
  /** Current battery kWh (for Backup Readiness when grid_primary). */
  availableKwh?: number | null;
  /** Current load kW (for runtime estimate when grid_primary). */
  loadKw?: number | null;
  /** Phase 2: resilience risk active (grid_primary only). */
  resilienceRisk?: boolean;
  /** Phase 2: 'warning' | 'critical' when resilienceRisk. */
  resilienceSeverity?: 'warning' | 'critical' | null;
  /** Override: use forecast-runway sunrise values for consistency with EnergyOutlook chart.
   *  When provided, these values take precedence over overnight_forecasts table data. */
  runwaySunriseKwh?: { expected: number; best: number; worst: number } | null;
}) {
  const isGridPrimary = gridMode === 'grid_primary';

  if (readinessState === 'connecting' || readinessState === 'degraded') {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
            {isGridPrimary ? (
              <Shield className="h-4 w-4 text-wt-text-secondary" />
            ) : (
              <Moon className="h-4 w-4 text-wt-text-secondary" />
            )}
            {isGridPrimary ? 'Backup Readiness' : 'Overnight outlook'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-wt-text-secondary">
            {readinessState === 'connecting'
              ? 'Forecast available after system connects.'
              : 'Forecast unavailable while data is outdated. Check connection or refresh.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Grid-primary: Backup Readiness card (no overnight forecast semantics)
  if (isGridPrimary) {
    const runtimeLine = formatBackupReadinessRuntime(availableKwh ?? 0, loadKw ?? 0);
    const runtimeText = runtimeLine ?? (loadKw != null && loadKw <= LOAD_EPSILON_KW ? 'Load minimal — runtime not estimated' : null);
    return (
      <Card>
        {resilienceRisk && (
          <div
            className={cn(
              'px-4 py-2 text-sm font-medium',
              resilienceSeverity === 'critical'
                ? 'bg-destructive/10 text-destructive border-b border-destructive/20'
                : 'bg-wt-amber-primary/10 text-wt-amber-primary border-b border-wt-amber-primary/20'
            )}
          >
            {resilienceCopy(resilienceSeverity ?? 'warning', {
              loadKw: loadKw ?? undefined,
            })}
          </div>
        )}
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
            <Shield className="h-4 w-4 text-wt-text-secondary" />
            Backup Readiness
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          <p className="text-lg font-medium text-foreground">
            <span className="font-mono-numeric">{availableKwh != null ? availableKwh.toFixed(1) : '—'}</span> kWh available
          </p>
          {runtimeText && (
            <p className="text-sm text-wt-text-secondary">
              {runtimeText}
            </p>
          )}
          <p className="text-sm text-wt-text-secondary">
            Applies if grid power is unavailable.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
            <Moon className="h-4 w-4 text-wt-text-secondary" />
            Overnight outlook
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center gap-2 text-wt-text-secondary text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Calculating overnight forecast…</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
            <Moon className="h-4 w-4 text-wt-text-secondary" />
            Overnight outlook
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-wt-text-secondary">Forecast unavailable. Try again later.</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
            <Moon className="h-4 w-4 text-wt-text-secondary" />
            Overnight outlook
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-wt-text-secondary">No overnight forecast yet.</p>
        </CardContent>
      </Card>
    );
  }

  // Use forecast-runway values when available for consistency with EnergyOutlook chart.
  const displayExpected = runwaySunriseKwh?.expected ?? data.expected_kwh;
  const displayLower = runwaySunriseKwh?.worst ?? data.lower_bound_kwh;
  const displayUpper = runwaySunriseKwh?.best ?? data.upper_bound_kwh;

  const band = `${displayLower.toFixed(1)}–${displayUpper.toFixed(1)} kWh`;
  const displayConfidence = capConfidence(data.confidence, readinessState, data.confidenceBasis);
  // Unproven ("establishing baseline") renders neutral, never the alarming low styling.
  const confidenceClass =
    !data.confidenceProven
      ? 'bg-muted/50 text-wt-text-secondary border-border'
      : displayConfidence === 'high'
        ? 'bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20'
        : displayConfidence === 'medium'
          ? 'bg-wt-amber-primary/10 text-wt-amber-primary border-wt-amber-primary/20'
          : 'bg-muted/50 text-wt-text-secondary border-border';

  // When current charge is 0 but forecast shows positive at sunrise, clarify: forecast is a prediction, not current state.
  const currentZeroButForecastPositive = availableKwh != null && availableKwh <= 0.1 && displayExpected > 0.5;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
          <Moon className="h-4 w-4 text-wt-text-secondary" />
          Overnight outlook
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        <p className="text-lg font-medium text-foreground">
          About <span className="font-mono-numeric">{displayExpected.toFixed(1)}</span> kWh at sunrise
        </p>
        <p className="text-sm text-wt-text-secondary">
          Expected range: <span className="font-mono-numeric">{band}</span>
        </p>
        {currentZeroButForecastPositive && (
          <p className="text-sm text-wt-text-secondary">
            Current charge is 0; this forecast is based on typical overnight use and may not apply.
          </p>
        )}
        <Badge variant="outline" className={`text-sm ${confidenceClass}`}>
          {confidenceLabel(displayConfidence, data.confidenceProven)}
        </Badge>
      </CardContent>
    </Card>
  );
}
